import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { getIsMobile } from '../../../../../utils/getIsMobile';
import { useTranslation } from '../../../../../utils/useTranslation';
import { Box } from '../../../../layout/Box';
import { MenuItem } from '../../../../navigation/Menu';
import { Typography } from '../../../../typography/Typography';
import { FeedManagementDateDrawer } from '../FeedManagementDateDrawer/FeedManagementDateDrawer';
import { FeedManagementDatePopup } from '../FeedManagementDatePopup/FeedManagementDatePopup';
import { FeedManagementDropdown } from '../FeedManagementDropdown/FeedManagementDropdown';
import { PERIOD_OPTIONS } from './constants';
import { FeedManagementDateDropdownProps } from './types';
import { getDateDropdownPlaceholder } from './utils';

export const FeedManagementDateDropdown: React.FC<FeedManagementDateDropdownProps> = ({
    value,
    dateFrom,
    dateTo,
    onChange,
    dataTestId,
}) => {
    const t = useTranslation();
    const { isMobile } = getIsMobile();

    const [visibleDatePopup, setVisibleDatePopup] = useState(false);
    const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);

    const onClick = useCallback(() => setVisibleDatePopup(true), []);
    const onClose = useCallback(() => setVisibleDatePopup(false), []);

    const onChangeDate = useCallback(
        (from?: string, to?: string) => {
            onClose();
            onChange(null, from, to);
        },
        [onChange, onClose],
    );

    const onChangeDropdown = useCallback(
        (newValue: string | null) => {
            if (newValue === 'undefined') {
                onChange(null);

                return;
            }
            onChange(newValue);
        },
        [onChange],
    );

    return (
        <Box ref={setReferenceElement}>
            <FeedManagementDropdown
                value={value}
                placeholder={getDateDropdownPlaceholder(value, dateFrom, dateTo)}
                onChange={onChangeDropdown}
                options={PERIOD_OPTIONS}
                dataTestId={dataTestId}
                additionalOption={
                    <MenuItem
                        key="date"
                        spacing={1}
                        direction="row"
                        justifyContent="space-between"
                        onClick={onClick}
                    >
                        <Typography variant="body" noWrap>
                            {t('Выбрать даты')}
                        </Typography>
                    </MenuItem>
                }
            />
            {visibleDatePopup
                ? createPortal(
                      isMobile ? (
                          <FeedManagementDateDrawer
                              dateFrom={dateFrom}
                              dateTo={dateTo}
                              onChange={onChangeDate}
                              onClose={onClose}
                          />
                      ) : (
                          <FeedManagementDatePopup
                              referenceElement={referenceElement}
                              dateFrom={dateFrom}
                              dateTo={dateTo}
                              onChange={onChangeDate}
                              onClose={onClose}
                          />
                      ),
                      document.body,
                  )
                : null}
        </Box>
    );
};
